import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IBroadcast } from 'modules/core/supabase/tables/broadcast';
import { Event } from '../../domain/interfaces/events';

export type OverlaySliceState = {
  isLoading: boolean;
  isLoaded: boolean;

  broadcastId?: number;
  deviceId?: number;
  tournamentId?: number;
  championshipId?: number;
  matchId?: number;
  templateId: number | null;

  featureIsAvailable?: boolean;
  isBoardLocked: boolean;
  isHideControlPanel: boolean;
  isMatchAvailable: boolean | undefined;
  isBroadcastInitialized: boolean;
  isMatchDataReady: boolean;

  events: Event[];
  passedEventsId: number[];
  activeEventId: number | null;

  popup: Window | null;
  popupIsLoaded: boolean;
};

const initialState: OverlaySliceState = {
  isLoading: false,
  isLoaded: false,
  isBoardLocked: false,
  isHideControlPanel: false,
  isMatchAvailable: undefined,
  isBroadcastInitialized: false,
  isMatchDataReady: false,

  templateId: null,

  events: [],
  passedEventsId: [],
  activeEventId: null,

  popup: null,
  popupIsLoaded: false,
};

export const overlaySlice = createSlice({
  name: 'overlay-v2',
  initialState,
  reducers: {
    setEvents(state, action: { payload: Event[] }) {
      state.events = action.payload;
    },
    addEvents(state, action: PayloadAction<Event[]>) {
      const events = action.payload.filter((event) => !state.passedEventsId.includes(event.id));

      state.events.push(...events);
      state.passedEventsId.push(...events.map((event) => event.id));
    },
    shiftEvent(state) {
      state.events = state.events.slice(1);
    },
    setBoardLocked(state, action: PayloadAction<boolean>) {
      state.isBoardLocked = action.payload;
    },
    setHideControlPanel(state, action: PayloadAction<boolean>) {
      state.isHideControlPanel = action.payload;
    },
    setActiveEventId(state, action: PayloadAction<number | null>) {
      state.activeEventId = action.payload;
    },
    setPassedEventsId(state, action: PayloadAction<number[]>) {
      state.passedEventsId = action.payload;
    },
    setPopup(state, action: PayloadAction<Window | null>) {
      state.popup = action.payload as any;
    },
    setPopupIsLoaded(state, action: PayloadAction<boolean>) {
      state.popupIsLoaded = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setIsLoaded(state, action: PayloadAction<boolean>) {
      state.isLoaded = action.payload;
    },
    setIsMatchAvailable(state, action: PayloadAction<boolean | undefined>) {
      state.isMatchAvailable = action.payload;
    },
    setIsBroadcastInitialized(state, action: PayloadAction<boolean>) {
      state.isBroadcastInitialized = action.payload;
    },
    setIsMatchDataReady(state, action: PayloadAction<boolean>) {
      state.isMatchDataReady = action.payload;
    },
    setBroadcastState(state, action: PayloadAction<IBroadcast | undefined>) {
      if (!action.payload) {
        state.broadcastId = undefined;
        state.deviceId = undefined;
        state.tournamentId = undefined;
        state.championshipId = undefined;
        state.matchId = undefined;
        state.templateId = null;
        return;
      }

      const newMatchId = action.payload.matchId ?? undefined;
      if (state.matchId !== newMatchId) {
        state.isMatchDataReady = false;
        state.passedEventsId = [];
        state.events = [];
        state.activeEventId = null;
      }

      state.broadcastId = action.payload.id ?? undefined;
      state.deviceId = action.payload.deviceId ?? undefined;
      state.tournamentId = action.payload.tournamentId ?? undefined;
      state.championshipId = action.payload.championshipId ?? undefined;
      state.matchId = newMatchId;
      state.templateId = action.payload.templateId ?? null;
    },
  },
});

export const {
  setEvents,
  setBoardLocked,
  setHideControlPanel,
  addEvents,
  shiftEvent,
  setActiveEventId,
  setPassedEventsId,
  setPopup,
  setPopupIsLoaded,
  setIsLoading,
  setIsLoaded,
  setBroadcastState,
  setIsMatchAvailable,
  setIsBroadcastInitialized,
  setIsMatchDataReady,
} = overlaySlice.actions;
